import React, { useState } from 'react';

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

function describeFieldingAction(action) {
  switch (action.type) {
    case 'putout': return 'Putout';
    case 'assist': return 'Assist';
    case 'error': return `Error (${action.errorType || 'unspecified'})`;
    case 'dp': return 'Double Play';
    case 'tp': return 'Triple Play';
    case 'passedBall': return 'Passed Ball';
    case 'strikeout': return 'Strikeout (K)';
    case 'blockedBall': return 'Blocked Ball';
    case 'droppedThird': {
      if (action.result === 'tagged') return 'Dropped 3rd — Tagged runner';
      if (action.result === 'threwOut') return 'Dropped 3rd — Threw out at 1st';
      if (action.result === 'safe' && action.errorOn === 'pitcher') return 'Dropped 3rd — Wild Pitch (safe)';
      if (action.result === 'safe' && action.errorOn === 'catcher') return 'Dropped 3rd — Catcher error (safe)';
      return 'Dropped 3rd Strike';
    }
    case 'throwAttempt': {
      const base = action.base === 'second' ? '2nd' : '3rd';
      if (action.result === 'cs') return `Steal Attempt — CS (${base})`;
      if (action.result === 'sbAllowed') return `Steal Attempt — SB Allowed (${base})`;
      if (action.result === 'error') return `Steal Attempt — Error (${base})`;
      return `Steal Attempt (${base})`;
    }
    case 'pickoffAttempt': {
      const base = action.base === 'first' ? '1st' : action.base === 'second' ? '2nd' : '3rd';
      if (action.result === 'pickoff') return `Pickoff — Out (${base})`;
      if (action.result === 'error') return `Pickoff — Error (${base})`;
      return `Pickoff Attempt — Safe (${base})`;
    }
    case 'stealHome': {
      if (action.how === 'steal') return 'Steal Home — Scored';
      if (action.how === 'passedBall') return 'Steal Home — Passed Ball';
      return 'Steal Home — Wild Pitch';
    }
    default: return action.type;
  }
}

function groupByPosition(actions) {
  const groups = {};
  for (const action of actions) {
    const pos = action.position || 'Unknown';
    if (!groups[pos]) groups[pos] = [];
    groups[pos].push(action);
  }
  return groups;
}

// ─────────────────────────────────────────────
// EDIT FIELDING ACTION MODAL
// ─────────────────────────────────────────────

function EditFieldingActionModal({ action, onSave, onDelete, onClose }) {
  // For standard fielding actions
  const [statKey, setStatKey] = useState(() => {
    if (action.type === 'error') return action.errorType === 'throwing' ? 'error_throwing' : 'error_fielding';
    if (action.type === 'putout' && action.situation === 'putout_assist') return 'putout_assist';
    return action.type;
  });

  // For throw attempt
  const [throwBase, setThrowBase] = useState(action.base || 'second');
  const [throwResult, setThrowResult] = useState(action.result || 'sbAllowed');

  // For pickoff attempt
  const [pickoffBase, setPickoffBase] = useState(action.base || 'first');
  const [pickoffResult, setPickoffResult] = useState(action.result || 'safe');

  // For dropped third
  const [d3kResult, setD3kResult] = useState(action.result || 'tagged');
  const [d3kErrorOn, setD3kErrorOn] = useState(action.errorOn || 'pitcher');

  // For steal home
  const [stealHow, setStealHow] = useState(action.how || 'steal');

  function handleSave() {
    let changes = {};
    if (action.type === 'throwAttempt') {
      changes = { base: throwBase, result: throwResult };
    } else if (action.type === 'pickoffAttempt') {
      changes = { base: pickoffBase, result: pickoffResult };
    } else if (action.type === 'droppedThird') {
      changes = { result: d3kResult, errorOn: d3kResult === 'safe' ? d3kErrorOn : undefined };
    } else if (action.type === 'stealHome') {
      changes = { how: stealHow };
    } else {
      // Standard stat
      if (statKey === 'putout') changes = { type: 'putout', situation: 'standard', errorType: undefined };
      else if (statKey === 'assist') changes = { type: 'assist', situation: 'standard', errorType: undefined };
      else if (statKey === 'putout_assist') changes = { type: 'putout', situation: 'putout_assist', errorType: undefined };
      else if (statKey === 'error_fielding') changes = { type: 'error', errorType: 'fielding', situation: 'fielding_error' };
      else if (statKey === 'error_throwing') changes = { type: 'error', errorType: 'throwing', situation: 'throwing_error' };
    }
    onSave(action.id, changes);
    onClose();
  }

  function renderEditFields() {
    if (action.type === 'throwAttempt') {
      return (
        <>
          <div className="fld-modal-body" style={{ marginBottom: 6 }}>Base</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {['second', 'third'].map(b => (
              <button key={b} className="fld-modal-confirm"
                style={{ flex: 1, background: throwBase === b ? 'var(--amber)' : 'var(--surface-3)', color: throwBase === b ? '#000' : 'var(--text-2)' }}
                onClick={() => setThrowBase(b)}>
                {b === 'second' ? '2nd' : '3rd'}
              </button>
            ))}
          </div>
          <div className="fld-modal-body" style={{ marginBottom: 6 }}>Result</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {[
              { key: 'sbAllowed', label: 'SB Allowed' },
              { key: 'cs', label: 'CS' },
              { key: 'error', label: 'Error' },
            ].map(({ key, label }) => (
              <button key={key} className="fld-modal-confirm"
                style={{ flex: 1, background: throwResult === key ? 'var(--amber)' : 'var(--surface-3)', color: throwResult === key ? '#000' : 'var(--text-2)' }}
                onClick={() => setThrowResult(key)}>
                {label}
              </button>
            ))}
          </div>
        </>
      );
    }

    if (action.type === 'pickoffAttempt') {
      return (
        <>
          <div className="fld-modal-body" style={{ marginBottom: 6 }}>Base</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {['first', 'second', 'third'].map(b => (
              <button key={b} className="fld-modal-confirm"
                style={{ flex: 1, background: pickoffBase === b ? 'var(--amber)' : 'var(--surface-3)', color: pickoffBase === b ? '#000' : 'var(--text-2)' }}
                onClick={() => setPickoffBase(b)}>
                {b === 'first' ? '1st' : b === 'second' ? '2nd' : '3rd'}
              </button>
            ))}
          </div>
          <div className="fld-modal-body" style={{ marginBottom: 6 }}>Result</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {[
              { key: 'safe', label: 'Safe' },
              { key: 'pickoff', label: 'Pickoff Out' },
              { key: 'error', label: 'Error' },
            ].map(({ key, label }) => (
              <button key={key} className="fld-modal-confirm"
                style={{ flex: 1, background: pickoffResult === key ? 'var(--amber)' : 'var(--surface-3)', color: pickoffResult === key ? '#000' : 'var(--text-2)' }}
                onClick={() => setPickoffResult(key)}>
                {label}
              </button>
            ))}
          </div>
        </>
      );
    }

    if (action.type === 'droppedThird') {
      return (
        <>
          <div className="fld-modal-body" style={{ marginBottom: 6 }}>Result</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {[
              { key: 'tagged', label: 'Tagged runner' },
              { key: 'threwOut', label: 'Threw out at 1st' },
              { key: 'safe', label: 'Runner safe' },
            ].map(({ key, label }) => (
              <button key={key} className="fld-modal-confirm"
                style={{ flex: 1, background: d3kResult === key ? 'var(--amber)' : 'var(--surface-3)', color: d3kResult === key ? '#000' : 'var(--text-2)', fontSize: 12 }}
                onClick={() => setD3kResult(key)}>
                {label}
              </button>
            ))}
          </div>
          {d3kResult === 'safe' && (
            <>
              <div className="fld-modal-body" style={{ marginBottom: 6 }}>Error on</div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                {[
                  { key: 'pitcher', label: 'Pitcher (WP)' },
                  { key: 'catcher', label: 'Catcher (E)' },
                ].map(({ key, label }) => (
                  <button key={key} className="fld-modal-confirm"
                    style={{ flex: 1, background: d3kErrorOn === key ? 'var(--amber)' : 'var(--surface-3)', color: d3kErrorOn === key ? '#000' : 'var(--text-2)', fontSize: 12 }}
                    onClick={() => setD3kErrorOn(key)}>
                    {label}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      );
    }

    if (action.type === 'stealHome') {
      return (
        <>
          <div className="fld-modal-body" style={{ marginBottom: 6 }}>How did they score?</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {[
              { key: 'steal', label: 'Straight Steal' },
              { key: 'passedBall', label: 'Passed Ball' },
              { key: 'wildPitch', label: 'Wild Pitch' },
            ].map(({ key, label }) => (
              <button key={key} className="fld-modal-confirm"
                style={{ flex: 1, background: stealHow === key ? 'var(--amber)' : 'var(--surface-3)', color: stealHow === key ? '#000' : 'var(--text-2)', fontSize: 12 }}
                onClick={() => setStealHow(key)}>
                {label}
              </button>
            ))}
          </div>
        </>
      );
    }

    // strikeout, blockedBall, passedBall — no editable fields, just confirm or delete
    if (['strikeout', 'blockedBall', 'passedBall'].includes(action.type)) {
      return (
        <div className="fld-modal-body" style={{ marginBottom: 14 }}>
          No editable fields for this action. You can delete it if it was logged in error.
        </div>
      );
    }

    // Standard: putout / assist / putout_assist / error
    return (
      <>
        <div className="fld-modal-body" style={{ marginBottom: 6 }}>Stat</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {[
            { key: 'putout', label: 'Putout' },
            { key: 'assist', label: 'Assist' },
            { key: 'putout_assist', label: 'Putout + Assist' },
            { key: 'error_fielding', label: 'Fielding Error' },
            { key: 'error_throwing', label: 'Throwing Error' },
          ].map(({ key, label }) => (
            <button key={key} className="fld-modal-confirm"
              style={{ background: statKey === key ? 'var(--amber)' : 'var(--surface-3)', color: statKey === key ? '#000' : 'var(--text-2)' }}
              onClick={() => setStatKey(key)}>
              {label}
            </button>
          ))}
        </div>
      </>
    );
  }

  return (
    <div className="fld-modal-overlay" onClick={onClose}>
      <div className="fld-modal-card" onClick={e => e.stopPropagation()}>
        <div className="fld-modal-title">Edit Action</div>
        <div className="fld-modal-body" style={{ marginBottom: 12, color: 'var(--amber)' }}>
          {action.position} — {describeFieldingAction(action)}
        </div>
        {renderEditFields()}
        <button className="fld-modal-confirm" style={{ width: '100%', marginBottom: 8 }} onClick={handleSave}>
          Save Changes
        </button>
        <button
          className="fld-modal-confirm"
          style={{ width: '100%', marginBottom: 8, background: 'rgba(248,113,113,0.15)', color: 'var(--red)', border: '1px solid rgba(248,113,113,0.4)' }}
          onClick={() => { onDelete(action.id); onClose(); }}
        >
          Delete Action
        </button>
        <button className="fld-modal-cancel" style={{ width: '100%' }} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// FIELDING LOG MODAL
// ─────────────────────────────────────────────

export default function FieldingLogModal({ isOpen, onClose, actions = [], positions = {}, onEdit, onDelete }) {
  const [editingAction, setEditingAction] = useState(null);

  if (!isOpen) return null;

  const groups = groupByPosition(actions);

  return (
    <>
      <div className="fld-modal-overlay" onClick={onClose}>
        <div className="fld-modal-card" onClick={e => e.stopPropagation()}>
          <div className="fld-log-header">
            <div className="fld-modal-title">Fielding Log</div>
            <button className="fld-log-close" onClick={onClose}>✕</button>
          </div>

          {actions.length === 0 ? (
            <div className="fld-modal-body">No fielding actions logged yet.</div>
          ) : (
            <div className="fld-log-body">
              {Object.entries(groups).map(([pos, posActions]) => {
                const posData = positions[pos];
                const ip = posData?.innings > 0 ? ` · ${posData.innings.toFixed(1)} IP` : '';
                return (
                  <div key={pos} className="fld-log-group">
                    <div className="fld-log-pos-header">{pos}{ip}</div>
                    {posActions.map((action) => (
                      <button
                        key={action.id}
                        className="fld-log-row"
                        onClick={() => setEditingAction(action)}
                      >
                        <span className="fld-log-action-label">{describeFieldingAction(action)}</span>
                        <span className="fld-log-edit-hint">✎</span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          <div className="fld-modal-actions">
            <button className="fld-modal-cancel" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>

      {editingAction && (
        <EditFieldingActionModal
          action={editingAction}
          onSave={(id, changes) => { onEdit(id, changes); setEditingAction(null); }}
          onDelete={(id) => { onDelete(id); setEditingAction(null); }}
          onClose={() => setEditingAction(null)}
        />
      )}
    </>
  );
}
